import React, { useState, useEffect } from 'react'
import {
  LayoutDashboard, CreditCard, Shield, Wallet,
  TrendingUp, BarChart2, Landmark, Calculator,
  PiggyBank, Star, Clock, ArrowLeftRight, Bell,
  User, LogOut, ChevronLeft, ChevronRight, Settings,
  Download, MoreHorizontal, X, MessageSquare, Target,
} from 'lucide-react'

const GOLD = '#c9a84c'

const NAV_GROUPS = [
  {
    section: null,
    items: [
      { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
      { id: 'forum',     label: 'Clubhouse', Icon: MessageSquare },
    ],
  },
  {
    section: 'Debt Management',
    items: [
      { id: 'loans',     label: 'Loans',                Icon: CreditCard },
      { id: 'payoff',    label: 'Smart Payoff Engine',  Icon: Target },
      { id: 'history',   label: 'Loan Payment History', Icon: Clock },
      { id: 'expenses',  label: 'Expenses',             Icon: Wallet },
      { id: 'compare',   label: 'Loan Comparison',      Icon: ArrowLeftRight },
      { id: 'reminders', label: 'Loan Reminders',       Icon: Bell },
    ],
  },
  {
    section: 'Wealth Building',
    items: [
      { id: 'insurance',   label: 'Insurance',     Icon: Shield },
      { id: 'emergency',   label: 'Emergency Fund', Icon: Landmark },
      { id: 'investments', label: 'Investments',    Icon: TrendingUp },
      { id: 'networth',    label: 'Net Worth',      Icon: BarChart2 },
    ],
  },
  {
    section: 'Tools',
    items: [
      { id: 'calculator', label: 'EMI Calculator',    Icon: Calculator },
      { id: 'budget',     label: 'Budget Planner',    Icon: PiggyBank },
      { id: 'credit',     label: 'Credit Score Tips', Icon: Star },
    ],
  },
]

const BOTTOM_NAV = [
  { id: 'dashboard',   label: 'Home',    Icon: LayoutDashboard },
  { id: 'loans',       label: 'Loans',   Icon: CreditCard },
  { id: 'investments', label: 'Invest',  Icon: TrendingUp },
  { id: 'emergency',   label: 'Fund',    Icon: Landmark },
  { id: 'profile',     label: 'Profile', Icon: User },
]

function NavBtn({ id, label, Icon, active, collapsed, onClick }) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? label : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.7rem',
        width: '100%',
        padding: collapsed ? '0.62rem 0' : '0.62rem 1.2rem',
        justifyContent: collapsed ? 'center' : 'flex-start',
        background: active ? 'rgba(201,168,76,0.11)' : 'transparent',
        border: 'none',
        borderLeft: `3px solid ${active ? GOLD : 'transparent'}`,
        cursor: 'pointer',
        color: active ? GOLD : 'var(--text)',
        fontFamily: 'monospace',
        fontSize: '0.72rem',
        fontWeight: active ? '600' : '400',
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
        textAlign: 'left',
        transition: 'background 0.12s, color 0.12s, border-color 0.12s',
      }}
      onMouseEnter={e => {
        if (!active) {
          e.currentTarget.style.background = 'var(--surface-hdr)'
        }
      }}
      onMouseLeave={e => {
        if (!active) {
          e.currentTarget.style.background = 'transparent'
        }
      }}
    >
      <Icon size={15} strokeWidth={active ? 2.5 : 1.75} style={{ flexShrink: 0 }} />
      {!collapsed && <span>{label}</span>}
    </button>
  )
}

export default function Sidebar({
  activePage,
  onNavigate,
  collapsed,
  onToggle,
  userName,
  userAvatar,
  onSignOut,
  showAdmin,
  onOpenAdmin,
  appSettings,
  exportRef,
  onExportClick,
  showExportMenu,
  exportMenuContent,
}) {
  const nameParts = (appSettings?.app_name || 'Debt Crush').split(' ')
  const W = collapsed ? 60 : 240

  const [isMobile, setIsMobile] = useState(() => {
    try { return window.innerWidth <= 768 } catch { return false }
  })
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // ── Mobile: bottom nav bar + slide-up drawer ──────────────
  if (isMobile) {
    const drawerBtn = (active) => ({
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      width: '100%', padding: '0.8rem 1.25rem',
      background: active ? 'rgba(201,168,76,0.11)' : 'transparent',
      border: 'none',
      borderLeft: `3px solid ${active ? GOLD : 'transparent'}`,
      cursor: 'pointer',
      color: active ? GOLD : 'var(--text)',
      fontFamily: 'monospace', fontSize: '0.85rem',
      fontWeight: active ? '600' : '400',
      textAlign: 'left',
    })

    return (
      <>
        {/* ── Bottom nav bar ── */}
        <nav style={{
          position: 'fixed',
          bottom: 0, left: 0, right: 0,
          height: '60px',
          background: 'var(--surface)',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'stretch',
          zIndex: 200,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}>
          {BOTTOM_NAV.map(({ id, label, Icon }) => {
            const active = activePage === id
            return (
              <button
                key={id}
                onClick={() => onNavigate(id)}
                style={{
                  flex: 1,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  gap: '0.12rem',
                  background: 'none', border: 'none',
                  borderTop: `2px solid ${active ? GOLD : 'transparent'}`,
                  cursor: 'pointer',
                  color: active ? GOLD : 'var(--text)',
                  transition: 'color 0.15s',
                  padding: '0.3rem 0 0.35rem',
                  minWidth: 0,
                }}
              >
                <Icon size={18} strokeWidth={active ? 2.5 : 1.75} />
                <span style={{ fontFamily: 'monospace', fontSize: '0.5rem', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>{label}</span>
              </button>
            )
          })}

          {/* More */}
          <button
            onClick={() => setShowMobileMenu(true)}
            style={{
              flex: 1,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: '0.12rem',
              background: 'none', border: 'none',
              borderTop: '2px solid transparent',
              cursor: 'pointer',
              color: 'var(--text)',
              padding: '0.3rem 0 0.35rem',
            }}
          >
            <MoreHorizontal size={18} strokeWidth={1.75} />
            <span style={{ fontFamily: 'monospace', fontSize: '0.5rem', letterSpacing: '0.02em' }}>More</span>
          </button>
        </nav>

        {/* ── Slide-up "More" drawer ── */}
        {showMobileMenu && (
          <div
            onClick={() => setShowMobileMenu(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 500,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--surface)',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
                maxHeight: '78vh',
                display: 'flex', flexDirection: 'column',
                paddingBottom: 'env(safe-area-inset-bottom)',
              }}
            >
              {/* Drawer header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '1rem 1.25rem',
                borderBottom: '1px solid var(--border)',
                flexShrink: 0,
              }}>
                <span style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700', color: 'var(--text)' }}>
                  {nameParts[0]}{' '}<span style={{ color: GOLD, fontStyle: 'italic' }}>{nameParts[1] || ''}</span>
                </span>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem', display: 'flex', alignItems: 'center' }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Drawer nav items */}
              <div style={{ overflowY: 'auto', flex: 1, padding: '0.5rem 0' }}>
                {NAV_GROUPS.map(({ section, items }, gi) => (
                  <div key={gi}>
                    {section && (
                      <div style={{
                        fontFamily: 'monospace', fontSize: '0.57rem', fontWeight: '700',
                        textTransform: 'uppercase', letterSpacing: '0.12em',
                        color: 'var(--text-muted)', padding: '0.85rem 1.25rem 0.3rem', opacity: 0.65,
                      }}>
                        {section}
                      </div>
                    )}
                    {items.map(({ id, label, Icon }) => {
                      const active = activePage === id
                      return (
                        <button
                          key={id}
                          onClick={() => { onNavigate(id); setShowMobileMenu(false) }}
                          style={drawerBtn(active)}
                        >
                          <Icon size={16} strokeWidth={active ? 2.5 : 1.75} style={{ flexShrink: 0 }} />
                          {label}
                        </button>
                      )
                    })}
                  </div>
                ))}

                <div style={{ height: '1px', background: 'var(--border)', margin: '0.5rem 1.25rem' }} />

                {/* Export */}
                <div ref={exportRef}>
                  <button onClick={onExportClick} style={drawerBtn(false)}>
                    <Download size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
                    Export
                  </button>
                  {showExportMenu && (
                    <div style={{ paddingLeft: '3.25rem', paddingBottom: '0.25rem' }}>
                      {exportMenuContent}
                    </div>
                  )}
                </div>

                {/* Admin */}
                {showAdmin && (
                  <button
                    onClick={() => { onOpenAdmin(); setShowMobileMenu(false) }}
                    style={drawerBtn(false)}
                  >
                    <Settings size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
                    Admin Panel
                  </button>
                )}

                {/* Profile */}
                <button
                  onClick={() => { onNavigate('profile'); setShowMobileMenu(false) }}
                  style={drawerBtn(activePage === 'profile')}
                >
                  {userAvatar ? (
                    <img src={userAvatar} alt="" style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <User size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
                  )}
                  {userName}
                </button>

                {/* Sign out */}
                <button
                  onClick={onSignOut}
                  style={{ ...drawerBtn(false), color: '#b8441a' }}
                >
                  <LogOut size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // ── Desktop: fixed left sidebar ───────────────────────────
  return (
    <aside style={{
      position: 'fixed',
      left: 0, top: 0, bottom: 0,
      width: W,
      background: 'var(--surface)',
      borderRight: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 200,
      transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
      overflow: 'hidden',
    }}>

      {/* ── Logo + toggle ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        padding: collapsed ? '0 0' : '0 0.6rem 0 1.25rem',
        height: '58px',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        {collapsed ? (
          <span style={{ fontFamily: 'Georgia, serif', fontSize: '1.1rem', fontWeight: '900', color: GOLD }}>
            {nameParts[0]?.charAt(0)}
          </span>
        ) : (
          <span style={{ fontFamily: 'Georgia, serif', fontSize: '1.1rem', fontWeight: '900', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden' }}>
            {nameParts[0]}{' '}
            <span style={{ color: GOLD, fontStyle: 'italic' }}>{nameParts[1] || ''}</span>
          </span>
        )}
        <button
          onClick={onToggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', padding: '0.35rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '5px', flexShrink: 0,
            transition: 'color 0.15s, background 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'var(--surface-hdr)' }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none' }}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
        </button>
      </div>

      {/* ── Nav groups ── */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '0.5rem 0' }}>
        {NAV_GROUPS.map(({ section, items }, gi) => (
          <div key={gi}>
            {section && !collapsed && (
              <div style={{
                fontFamily: 'monospace', fontSize: '0.57rem', fontWeight: '700',
                textTransform: 'uppercase', letterSpacing: '0.12em',
                color: 'var(--text-muted)', padding: '0.85rem 1.25rem 0.3rem', opacity: 0.65,
              }}>
                {section}
              </div>
            )}
            {section && collapsed && gi > 0 && (
              <div style={{ height: '1px', background: 'var(--border)', margin: '0.45rem 0.85rem' }} />
            )}
            {items.map(({ id, label, Icon }) => (
              <NavBtn
                key={id}
                id={id}
                label={label}
                Icon={Icon}
                active={activePage === id}
                collapsed={collapsed}
                onClick={() => onNavigate(id)}
              />
            ))}
          </div>
        ))}
      </div>

      {/* ── Bottom actions ── */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.35rem', paddingBottom: '0.5rem', flexShrink: 0, position: 'relative' }}>

        {/* Export */}
        <div ref={exportRef} style={{ position: 'relative' }}>
          <NavBtn id="export" label="Export" Icon={Download} active={false} collapsed={collapsed} onClick={onExportClick} />
          {showExportMenu && !collapsed && (
            <div style={{
              position: 'absolute', bottom: '100%', left: '1.2rem', marginBottom: '4px',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: '7px', boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
              minWidth: '200px', overflow: 'hidden', zIndex: 300,
            }}>
              {exportMenuContent}
            </div>
          )}
        </div>

        {/* Admin */}
        {showAdmin && (
          <NavBtn id="admin" label="Admin Panel" Icon={Settings} active={false} collapsed={collapsed} onClick={onOpenAdmin} />
        )}

        {/* Profile */}
        <button
          onClick={() => onNavigate('profile')}
          title={collapsed ? userName : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.7rem',
            width: '100%', padding: collapsed ? '0.62rem 0' : '0.62rem 1.2rem',
            justifyContent: collapsed ? 'center' : 'flex-start',
            background: activePage === 'profile' ? 'rgba(201,168,76,0.11)' : 'transparent',
            border: 'none', borderLeft: `3px solid ${activePage === 'profile' ? GOLD : 'transparent'}`,
            cursor: 'pointer', color: activePage === 'profile' ? GOLD : 'var(--text)',
            fontFamily: 'monospace', fontSize: '0.72rem',
            fontWeight: activePage === 'profile' ? '600' : '400',
            whiteSpace: 'nowrap', textAlign: 'left', transition: 'background 0.12s, color 0.12s',
          }}
          onMouseEnter={e => { if (activePage !== 'profile') { e.currentTarget.style.background = 'var(--surface-hdr)' } }}
          onMouseLeave={e => { if (activePage !== 'profile') { e.currentTarget.style.background = 'transparent' } }}
        >
          {userAvatar ? (
            <img src={userAvatar} alt="" style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <User size={15} strokeWidth={1.75} style={{ flexShrink: 0 }} />
          )}
          {!collapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</span>}
        </button>

        {/* Sign out */}
        <button
          onClick={onSignOut}
          title={collapsed ? 'Sign Out' : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.7rem',
            width: '100%', padding: collapsed ? '0.62rem 0' : '0.62rem 1.2rem',
            justifyContent: collapsed ? 'center' : 'flex-start',
            background: 'transparent', border: 'none', borderLeft: '3px solid transparent',
            cursor: 'pointer', color: 'var(--text)', fontFamily: 'monospace',
            fontSize: '0.72rem', whiteSpace: 'nowrap', textAlign: 'left',
            transition: 'background 0.12s, color 0.12s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hdr)'; e.currentTarget.style.color = '#b8441a' }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text)' }}
        >
          <LogOut size={15} strokeWidth={1.75} style={{ flexShrink: 0 }} />
          {!collapsed && <span>Sign Out</span>}
        </button>

      </div>
    </aside>
  )
}
